/**
 * Voice Recording Hook
 * Handles push-to-talk voice recording with Space key
 */

import { useState, useCallback, useRef, useEffect } from 'react';

export interface VoiceRecordingState {
  isRecording: boolean;
  isProcessing: boolean;
  duration: number;
  error: string | null;
}

export interface UseVoiceRecordingOptions {
  onRecordingComplete?: (audioBlob: Blob) => void;
  onError?: (error: string) => void;
  maxDuration?: number; // Max recording duration in seconds (default: 30)
  sampleRate?: number; // Audio sample rate (default: 16000 for GLM-ASR)
}

export function useVoiceRecording(options: UseVoiceRecordingOptions = {}) {
  const {
    onRecordingComplete,
    onError,
    maxDuration = 30,
    sampleRate = 16000,
  } = options;

  const [state, setState] = useState<VoiceRecordingState>({
    isRecording: false,
    isProcessing: false,
    duration: 0,
    error: null,
  });

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
  }, []);

  // Start recording
  const startRecording = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, error: null, isRecording: true, duration: 0 }));

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      streamRef.current = stream;

      // Determine best supported format
      // Prefer WAV-compatible formats for GLM-ASR
      const mimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/mp4',
      ];

      let mimeType = '';
      for (const type of mimeTypes) {
        if (MediaRecorder.isTypeSupported(type)) {
          mimeType = type;
          break;
        }
      }

      if (!mimeType) {
        throw new Error('No supported audio format found');
      }

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        cleanup();
        setState(prev => ({ ...prev, isRecording: false }));

        if (audioBlob.size > 0) {
          onRecordingComplete?.(audioBlob);
        }
      };

      mediaRecorder.onerror = () => {
        const errorMessage = 'Recording error occurred';
        setState(prev => ({ ...prev, error: errorMessage, isRecording: false }));
        onError?.(errorMessage);
        cleanup();
      };

      // Start recording
      mediaRecorder.start(100); // Collect data every 100ms
      startTimeRef.current = Date.now();

      // Update duration every 100ms
      durationIntervalRef.current = setInterval(() => {
        const elapsed = (Date.now() - startTimeRef.current) / 1000;
        setState(prev => ({ ...prev, duration: elapsed }));

        // Auto-stop at max duration
        if (elapsed >= maxDuration) {
          stopRecording();
        }
      }, 100);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start recording';
      setState(prev => ({ ...prev, error: errorMessage, isRecording: false }));
      onError?.(errorMessage);
      cleanup();
    }
  }, [sampleRate, maxDuration, onRecordingComplete, onError, cleanup]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && state.isRecording) {
      mediaRecorderRef.current.stop();
    }
  }, [state.isRecording]);

  // Cancel recording (discard audio)
  const cancelRecording = useCallback(() => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onstop = null;
      if (state.isRecording) {
        mediaRecorderRef.current.stop();
      }
    }
    cleanup();
    setState(prev => ({ ...prev, isRecording: false, duration: 0 }));
  }, [state.isRecording, cleanup]);

  // Set processing state
  const setProcessing = useCallback((processing: boolean) => {
    setState(prev => ({ ...prev, isProcessing: processing }));
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    ...state,
    startRecording,
    stopRecording,
    cancelRecording,
    setProcessing,
  };
}

/**
 * Hook for handling Space key press-to-talk
 */
export function useSpaceToTalk(options: UseVoiceRecordingOptions & {
  enabled?: boolean;
  inputFocused?: boolean;
}) {
  const { enabled = true, inputFocused = false, ...recordingOptions } = options;
  const recording = useVoiceRecording(recordingOptions);
  const isSpaceHeldRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Only trigger if Space is pressed and not in an input field
      if (
        e.code === 'Space' &&
        !isSpaceHeldRef.current &&
        !recording.isRecording &&
        !recording.isProcessing &&
        !inputFocused &&
        !(e.target instanceof HTMLInputElement) &&
        !(e.target instanceof HTMLTextAreaElement)
      ) {
        e.preventDefault();
        isSpaceHeldRef.current = true;
        recording.startRecording();
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space' && isSpaceHeldRef.current) {
        e.preventDefault();
        isSpaceHeldRef.current = false;
        if (recording.isRecording) {
          recording.stopRecording();
        }
      }
    };

    // Handle window blur (user switches tab while holding space)
    const handleBlur = () => {
      if (isSpaceHeldRef.current) {
        isSpaceHeldRef.current = false;
        if (recording.isRecording) {
          recording.cancelRecording();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, [enabled, inputFocused, recording]);

  return recording;
}

export default useVoiceRecording;
